import type { InferInput, InferOutput } from "valibot"
import { image } from "@aschehoug/crystallize-utils"
import {
  array,
  boolean,
  literal,
  nullish,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  union,
} from "valibot"
import { toValue } from "vue"
import { convertToCase } from "../string"
import { baseCatalogueSchema } from "./catalogue"
import { genericComponents } from "./components"

export function transformPriceVariants(variants: { identifier: string, price: number }[]) {
  return variants.reduce((acc, { identifier, price }) => ({
    ...acc,
    [convertToCase(identifier, "camel")]: price,
  }), {})
}

export const priceVariantSchema = object({
  priceVariants: pipe(
    array(
      object({
        identifier: string(),
        price: number(),
      }),
    ),
    transform(transformPriceVariants),
    object({
      default: number(),
      discount: optional(number()),
      consumerPrice: optional(number()),
      consumerDiscount: optional(number()),
    }),
  ),
})

export const productVariantSchema = object({
  ...priceVariantSchema.entries,
  name: string(),
  sku: string(),
  isDefault: boolean(),
  // TODO: Add custom components if needed
  components: genericComponents,
  price: number(),
  firstImage: nullish(image),
  subscriptionPlans: nullish(
    array(
      object({
        identifier: string(),
        periods: array(
          object({
            id: string(),
            initial: nullish(priceVariantSchema),
            recurring: nullish(priceVariantSchema),
          }),
        ),
      }),
    ),
  ),
})

export type CrystallizeProductVariant = InferOutput<typeof productVariantSchema>

// TODO: Add custom components if needed
export const productSchema = object({
  ...baseCatalogueSchema.entries,
  type: literal("product"),
  vatType: object({
    name: string(),
    percent: number(),
  }),
  variants: array(productVariantSchema),
})

const rawIdSchema = nullish(union([string(), number()]))

const productIdentifierSchema = pipe(
  union([
    rawIdSchema,
    pipe(
      object({ ean: rawIdSchema, sku: rawIdSchema }),
      transform(it => it.ean || it.sku),
    ),
  ]),
  transform((value) => {
    if (typeof value === "string") {
      return value
    }
    if (typeof value === "number") {
      return String(value)
    }
    return null
  }),
)

export type CrystallizeProductIdentifier = InferInput<typeof productIdentifierSchema>

export type CrystallizeProductData = Map<string, CrystallizeProductVariant>

export function getEan(id: MaybeRefOrGetter<CrystallizeProductIdentifier>) {
  const ean = safeParse(productIdentifierSchema, toValue(id))
  if (!ean.success) {
    throw ean.issues
  }

  return ean.output
}

export function getProductPrice(
  id: MaybeRefOrGetter<CrystallizeProductIdentifier>,
  data: MaybeRefOrGetter<CrystallizeProductData | undefined>,
) {
  const ean = getEan(id)
  if (!ean) {
    return undefined
  }

  const variants = toValue(data)?.get(ean)?.priceVariants
  if (!variants) {
    return undefined
  }

  const discounted = variants.consumerDiscount ?? variants.discount
  const original = variants.consumerPrice ?? variants.default

  return {
    original,
    actual: discounted ?? original,
    discounted,
    discount: discounted && original
      ? {
          value: original - discounted,
          percentage: Math.floor(((original - discounted) / original) * 100),
        }
      : undefined,
  }
}

export type CrystallizeVariantPrice = Exclude<ReturnType<typeof getProductPrice>, undefined>
